/**
 * ExportsScreen — route `/rounds/:roundId/exports` (operator+).
 *
 * Download buttons for the round:
 *   - JSON: the full `BatchReport` (summary, rejection_breakdown, system_errors,
 *     embedded human_review_queue).
 *   - «Expedientes (CSV)»: every expediente of the round with its current
 *     verdict (PRD #341), read live — it works mid-round.
 *
 * We fetch each response as a Blob through the typed client (so the auth
 * middleware attaches the bearer token in cloud mode and the RFC-7807 error is
 * thrown as an `ApiError` we can map by its stable `code`), then trigger a
 * browser download.
 *
 * The report endpoint 404s mid-round (`ROUND_NOT_FOUND` / `REPORT_UNAVAILABLE`)
 * until `rounds.status=completed`; `ProblemAlert` already renders Spanish copy
 * for both codes — that IS the "report not ready yet" state.
 *
 * Generated operations used:
 *   - getRoundReport  GET /rounds/{round_id}/report?format=json
 *   - exportRound     GET /rounds/{round_id}/export?format=csv (the file is
 *     named by the server's Content-Disposition)
 *
 * DEMO mode (no VITE_API_BASE_URL): no real network exists, so we synthesize a
 * tiny sample Blob through the identical download path and show the DEMO badge.
 */

import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";

import { api, ApiError, IS_DEMO, type DocumentPackagePreviewResponse, type DocumentPackageResponse } from "../api/client";
import { useApiMutation } from "../api/useApi";
import { DemoBadge } from "../components/DemoBadge";
import { ProblemAlert } from "../components/ProblemAlert";
import { RoundTabs } from "../components/RoundTabs";
import { fetchRoundExport } from "./exports/roundExport";

/** Synthesizes a download from a Blob via a transient object URL. */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Release the object URL after the click has been processed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const reportFilename = (roundId: string) => `reporte-ronda-${roundId}.json`;

/** DEMO-only sample report so the download path works without a real API. */
function demoReportBlob(roundId: string): Blob {
  const report = {
    round_id: roundId,
    summary: { total: 2, approved: 0, rejected: 0, human_review: 2 },
    rejection_breakdown: [],
    human_review_queue: [],
    system_errors: [],
    _demo: true,
  };
  return new Blob([JSON.stringify(report, null, 2)], {
    type: "application/json",
  });
}

export default function ExportsScreen() {
  const { roundId } = useParams<{ roundId: string }>();
  const location = useLocation();

  // One mutation per button so JSON and CSV downloads spin independently and
  // surface their own error. `run` returns undefined and stores the ApiError on
  // failure (useApiMutation swallows the throw into `error`).
  const jsonDl = useApiMutation((rid: string) => downloadReport(rid));
  const csvDl = useApiMutation((rid: string) => downloadRoundExport(rid));
  const [packagePreview, setPackagePreview] = useState<DocumentPackagePreviewResponse | null>(null);
  const [roundPackage, setRoundPackage] = useState<DocumentPackageResponse | null>(null);
  const [packageError, setPackageError] = useState<ApiError | Error | null>(null);
  const [packageBusy, setPackageBusy] = useState(false);
  const [confirmPackage, setConfirmPackage] = useState(false);
  const [packageHidden, setPackageHidden] = useState(false);

  const loadPackagePreview = async () => {
    if (!roundId) return;
    if (IS_DEMO) {
      setPackagePreview({
        round_id: roundId, expediente_count: 3, document_count: 9, source_bytes: 12_480_000,
        closed_at: new Date(Date.now() - 20 * 60_000).toISOString(),
        eligible_at: new Date(Date.now() - 5 * 60_000).toISOString(),
        retention_deadline: null, eligible: true, latest_package: null,
      });
      return;
    }
    try {
      const response = await api.GET("/rounds/{round_id}/document-package-preview", {
        params: { path: { round_id: roundId } },
      });
      const preview = response.data!;
      setPackagePreview(preview);
      setRoundPackage(preview.latest_package ?? null);
      setPackageError(null);
    } catch (error) {
      if (error instanceof ApiError && error.code === "CUSTODY_MODE_MISMATCH") {
        setPackageHidden(true);
        return;
      }
      setPackageError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  useEffect(() => { void loadPackagePreview(); }, [roundId]); // preview never creates work

  useEffect(() => {
    if (!roundPackage || !roundId || !["QUEUED", "RUNNING"].includes(roundPackage.state)) return undefined;
    const timer = window.setInterval(() => {
      void api.GET("/document-packages/{package_id}", { params: { path: { package_id: roundPackage.package_id } } })
        .then((response) => setRoundPackage(response.data ?? null))
        .catch((error: unknown) => setPackageError(error instanceof Error ? error : new Error(String(error))));
    }, 2_500);
    return () => window.clearInterval(timer);
  }, [roundPackage, roundId]);

  const createRoundPackage = async () => {
    if (!roundId) return;
    setPackageBusy(true);
    try {
      if (IS_DEMO) {
        setRoundPackage({
          package_id: "demo-round-package", scope: "RONDA", round_id: roundId, expediente_id: null,
          state: "READY", attempt: 1, created_at: new Date().toISOString(), started_at: new Date().toISOString(),
          ready_at: new Date().toISOString(), expires_at: new Date(Date.now() + 86_400_000).toISOString(),
          files_total: 9, bytes_total: 12_480_000, files_processed: 9, bytes_processed: 12_480_000,
          failure_detail: null,
        });
      } else {
        const response = await api.POST("/rounds/{round_id}/document-packages", { params: { path: { round_id: roundId } } });
        setRoundPackage(response.data!);
        await loadPackagePreview();
      }
      setPackageError(null);
    } catch (error) {
      setPackageError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setPackageBusy(false);
      setConfirmPackage(false);
    }
  };

  const downloadRoundPackage = async () => {
    if (!roundPackage || !roundId) return;
    if (IS_DEMO) {
      triggerDownload(new Blob(["Vera DEMO round package\n"], { type: "application/zip" }), `ronda-${roundId}-documentos.zip`);
      return;
    }
    setPackageBusy(true);
    try {
      const response = await api.POST("/document-packages/{package_id}/download-links", {
        params: { path: { package_id: roundPackage.package_id } },
      });
      window.location.assign(response.data!.url);
      setPackageError(null);
    } catch (error) {
      setPackageError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setPackageBusy(false);
    }
  };

  /**
   * Fetches the JSON report as a Blob and triggers the download. In DEMO mode it
   * synthesizes a sample artifact instead.
   */
  async function downloadReport(rid: string): Promise<void> {
    if (IS_DEMO) {
      triggerDownload(demoReportBlob(rid), reportFilename(rid));
      return;
    }
    const res = await api.GET("/rounds/{round_id}/report", {
      params: { path: { round_id: rid }, query: { format: "json" } },
      parseAs: "blob",
    });
    // On success res.data is a Blob (parseAs: "blob"); non-2xx already threw.
    triggerDownload(res.data!, reportFilename(rid));
  }

  /** «Expedientes (CSV)»: every expediente, named by the server (DEMO: a sample). */
  async function downloadRoundExport(rid: string): Promise<void> {
    const { blob, filename } = await fetchRoundExport(rid, {
      demo: IS_DEMO,
      get: (roundId) =>
        api.GET("/rounds/{round_id}/export", {
          params: { path: { round_id: roundId }, query: { format: "csv" } },
          parseAs: "blob",
        }),
    });
    triggerDownload(blob, filename);
  }

  if (!roundId) {
    return (
      <Alert severity="error">
        <AlertTitle>Ronda no especificada</AlertTitle>
        Falta el identificador de la ronda en la URL.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <RoundTabs roundId={roundId} currentPath={location.pathname} />
      <Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="h1">Exportaciones</Typography>
          <DemoBadge />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Descarga el reporte de la ronda en JSON o todos los expedientes en
          CSV.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ronda: {roundId}
        </Typography>
      </Box>

      <Alert severity="info" variant="outlined">
        El reporte JSON queda disponible cuando la ronda termina de procesarse.
        El CSV de expedientes se puede descargar en cualquier momento, también
        con la ronda en curso.
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            {/* JSON export */}
            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <DataObjectIcon color="action" />
                  <Box>
                    <Typography variant="h3">
                      Reporte completo (JSON)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Resumen de KPIs, desglose de rechazos, errores de sistema
                      y la cola de revisión embebida.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => void jsonDl.run(roundId)}
                  disabled={jsonDl.loading}
                  sx={{ flexShrink: 0 }}
                >
                  {jsonDl.loading ? "Descargando…" : "Descargar JSON"}
                </Button>
              </Stack>
              {jsonDl.error && (
                <ProblemAlert
                  error={jsonDl.error}
                  onRetry={() => void jsonDl.run(roundId)}
                />
              )}
            </Box>

            <Divider />

            {/* CSV export */}
            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <TableChartOutlinedIcon color="action" />
                  <Box>
                    <Typography variant="h3">Expedientes (CSV)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Todos los expedientes de la ronda con su veredicto
                      vigente, motivos y datos del aspirante, listo para Excel.
                      Funciona aunque la ronda siga en curso.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => void csvDl.run(roundId)}
                  disabled={csvDl.loading}
                  sx={{ flexShrink: 0 }}
                >
                  {csvDl.loading ? "Descargando…" : "Descargar CSV"}
                </Button>
              </Stack>
              {csvDl.error && (
                <ProblemAlert
                  error={csvDl.error}
                  onRetry={() => void csvDl.run(roundId)}
                />
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {!packageHidden && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <FolderZipOutlinedIcon color="action" />
                <Box>
                  <Typography variant="h3">Documentos de la ronda</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paquete completo de los documentos en custodia Vera. No usa filtros del tablero ni veredictos.
                  </Typography>
                </Box>
              </Stack>
              {packagePreview && (
                <Typography variant="body2" color="text.secondary">
                  {packagePreview.expediente_count} expedientes · {packagePreview.document_count} documentos · {new Intl.NumberFormat("es-MX", { style: "unit", unit: "megabyte", maximumFractionDigits: 1 }).format(packagePreview.source_bytes / 1_000_000)} estimados
                </Typography>
              )}
              {packagePreview?.closed_at && <Typography variant="caption" color="text.secondary">Cerrada: {new Date(packagePreview.closed_at).toLocaleString("es-MX")}</Typography>}
              {packagePreview?.eligible_at && !packagePreview.eligible && <Alert severity="info">La ronda estará disponible después de la estabilización: {new Date(packagePreview.eligible_at).toLocaleString("es-MX")}</Alert>}
              {packagePreview?.retention_deadline && <Alert severity="warning">Retención documental hasta: {new Date(packagePreview.retention_deadline).toLocaleString("es-MX")}</Alert>}
              {roundPackage && ["QUEUED", "RUNNING"].includes(roundPackage.state) && (
                <Box>
                  <LinearProgress variant="determinate" value={roundPackage.bytes_total ? (roundPackage.bytes_processed / roundPackage.bytes_total) * 100 : 0} />
                  <Typography variant="caption" color="text.secondary">{roundPackage.state}: {roundPackage.files_processed}/{roundPackage.files_total} archivos</Typography>
                </Box>
              )}
              {roundPackage?.state === "FAILED" && <Alert severity="error">{roundPackage.failure_detail ?? "No se pudo generar el paquete."}</Alert>}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {roundPackage?.state === "READY" ? (
                  <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => void downloadRoundPackage()} disabled={packageBusy}>
                    {packageBusy ? "Preparando enlace…" : "Descargar documentos"}
                  </Button>
                ) : (
                  <Button variant="contained" startIcon={<FolderZipOutlinedIcon />} onClick={() => setConfirmPackage(true)} disabled={!packagePreview?.eligible || packageBusy}>
                    {roundPackage?.state === "FAILED" || roundPackage?.state === "EXPIRED" ? "Reintentar paquete" : "Preparar documentos"}
                  </Button>
                )}
                <Button variant="text" onClick={() => void loadPackagePreview()} disabled={packageBusy}>Actualizar</Button>
              </Stack>
              {packageError && <ProblemAlert error={packageError} onRetry={() => void loadPackagePreview()} />}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmPackage} onClose={() => setConfirmPackage(false)}>
        <DialogTitle>Preparar documentos de toda la ronda</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se generará un ZIP con todos los documentos reconocibles de la ronda ({packagePreview?.expediente_count ?? 0} expedientes, {packagePreview?.document_count ?? 0} documentos). Incluye PII, no respeta filtros y puede ocupar decenas de GB. El artefacto dura como máximo 24 horas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPackage(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void createRoundPackage()} disabled={packageBusy}>Confirmar y preparar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
